/* eslint-disable rxjs/no-ignored-subscription */
// We don't need unsubscribe observables in this service, because of using takeUntilDestroyed
import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, InteractionStatus, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { PromptValue } from '@azure/msal-common';
import { Store } from '@ngxs/store';
import { BehaviorSubject, catchError, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { PressureSensor, TenantService, UserTenantInformation } from '../shared/services/api';
import { SetTenantUser, UserLoggedIn } from '../shared/state/application.actions';
import { AssetInfo } from '../shared/state/pressure/models/asset-info';
import { GetConfiguration, GetPressureSensors, SetSelectedAsset } from '../shared/state/pressure/pressure.actions';
import { PressureSelectors } from '../shared/state/pressure/pressure.selectors';
import { defaultLocationKey } from '../tenant/models/settings-repository';
import { IdTokenClaimsWithPolicyId } from './Id-token-claims-with-policy-id';
import { APP_NAME, TENANT_ID } from './authorization-factories';

@Injectable()
export class AuthorizationService {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private tenantId: string = inject(TENANT_ID);
    private msalGuardConfig: MsalGuardConfiguration = inject(MSAL_GUARD_CONFIG) as unknown as MsalGuardConfiguration;
    private tenantService: TenantService = inject(TenantService);
    private store: Store = inject(Store);
    private authService: MsalService = inject(MsalService);
    private msalBroadcastService: MsalBroadcastService = inject(MsalBroadcastService);
    protected readonly allPressureSensors = this.store.selectSignal(PressureSelectors.getAllPressureSensor);
    protected readonly pressureAppConfiguration = this.store.selectSignal(PressureSelectors.getAllLocationConfiguration);

    private appName = inject(APP_NAME);

    private isLoginInProgress$$ = new BehaviorSubject<boolean>(false);
    private hasPermissions$$ = new BehaviorSubject<boolean>(false);

    public isLoginInProgress$ = this.isLoginInProgress$$.asObservable();
    public hasPermissions$ = this.hasPermissions$$.asObservable();
    public hasAnyPressureSensor = signal<boolean>(false);
    public isPressureSensorLoaded = signal<boolean>(false);
    private passwordResetPending = false;

    public subscribeLoginInProgress() {
        this.msalBroadcastService.inProgress$
            .pipe(
                tap((status: InteractionStatus) => {
                    if (status === InteractionStatus.None) {
                        this.checkAndSetActiveAccount();
                        return;
                    }

                    this.isLoginInProgress$$.next(true);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    public handleRedirectAfterLogin(eventMessage: EventMessage) {
        const payload = eventMessage.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (
            idtoken.acr?.toLowerCase() === environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.names.signUpSignIn.toLowerCase() ||
            idtoken.tfp?.toLowerCase() === environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.names.signUpSignIn.toLowerCase()
        ) {
            this.authService.instance.setActiveAccount(payload.account);
        }

        if (
            idtoken.acr?.toLowerCase() === environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.names.resetPassword.toLowerCase() ||
            idtoken.tfp?.toLowerCase() === environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.names.resetPassword.toLowerCase()
        ) {
            let signUpSignInFlowRequest: RedirectRequest | PopupRequest = {
                authority: environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.authorities.signUpSignIn.authority,
                scopes: [environment.AzureActiveDirectory_B2C_AngularConfig.AAD_ConsentScopes.connectPortalAPI, environment.AzureActiveDirectory_B2C_AngularConfig.AAD_ConsentScopes.openId],
                prompt: PromptValue.LOGIN,
            };

            this.login(signUpSignInFlowRequest);
        }
    }

    public handleRedirectPasswordReset(result: EventMessage) {
        // The user has forgotten their password. Need to initiate the password reset flow again.
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
            this.initiatePasswordReset();
        }
    }

    public handleAcquireToken() {
        const activeAccount = this.authService.instance.getActiveAccount() ?? this.authService.instance.getAllAccounts()?.[0];
        const sessionId = activeAccount?.idTokenClaims?.['sid'];

        setTimeout(() => {
            this.authService
                .acquireTokenRedirect({
                    sid: sessionId,
                    scopes: [environment.AzureActiveDirectory_B2C_AngularConfig.AAD_ConsentScopes.connectPortalAPI, environment.AzureActiveDirectory_B2C_AngularConfig.AAD_ConsentScopes.openId],
                })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe();
        });
    }

    private initiatePasswordReset() {
        if (this.passwordResetPending) {
            return;
        }

        this.passwordResetPending = true;
        const resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
            authority: environment.AzureActiveDirectory_B2C_AngularConfig.b2cPolicies.authorities.resetPassword.authority,
            scopes: [],
        };

        this.login(resetPasswordFlowRequest);
    }

    private checkAndSetActiveAccount() {
        let activeAccount = this.authService.instance.getActiveAccount();

        if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
            let accounts = this.authService.instance.getAllAccounts();
            this.authService.instance.setActiveAccount(accounts[0]);
        }

        if (!activeAccount) {
            if (!this.passwordResetPending) {
                this.login(undefined);
            }

            return;
        }

        this.store.dispatch(new UserLoggedIn(activeAccount));

        this.tenantService
            .getTenantUser(this.tenantId ?? '', 'response')
            .pipe(
                tap((tenantUserResult: any) => {
                    this.tenantPermissionsSucceeded(tenantUserResult.body);
                }),
                catchError((error) => {
                    this.tenantPermissionsFailed(error);
                    this.isLoginInProgress$$.next(false);
                    return throwError(() => error);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private login(userFlowRequest?: RedirectRequest | PopupRequest) {
        if (this.msalGuardConfig.authRequest) {
            this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
        } else {
            this.authService.loginRedirect(userFlowRequest);
        }
    }

    private tenantPermissionsSucceeded(userTenantInformation: UserTenantInformation) {
        this.store.dispatch(new SetTenantUser(userTenantInformation));

        this.store
            .dispatch([new GetPressureSensors(), new GetConfiguration(this.appName)])
            .pipe(
                tap(() => {
                    this.hasPermissions$$.next(true);
                    this.isPressureSensorLoaded.set(true);

                    const allPressureSensors = this.allPressureSensors();
                    this.setPressureSensor(allPressureSensors);

                    this.isLoginInProgress$$.next(false);
                }),
                catchError((error) => {
                    this.isLoginInProgress$$.next(false);
                    return throwError(() => error);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private setPressureSensor(allPressureSensors: PressureSensor[]) {
        if (!allPressureSensors?.length) {
            return;
        }

        const config = this.pressureAppConfiguration();
        const locationAssetId = config?.locations?.[defaultLocationKey()]?.assetId;
        const configuredSensor = allPressureSensors.find((pressureSensor) => pressureSensor.assetId === locationAssetId);
        const pressureSensor = configuredSensor ?? allPressureSensors?.[0];

        if (!pressureSensor) {
            return;
        }

        const { assetId, edgeDeviceId, telemetryDataType } = pressureSensor;
        this.store.dispatch(new SetSelectedAsset({ assetId, edgeDeviceId, telemetryDataType } as AssetInfo));

        this.hasAnyPressureSensor.set(true);
    }

    private tenantPermissionsFailed(request: HttpErrorResponse) {
        if (this.isRedirectLoop(request)) {
            return;
        }

        if ([401, 403, 404].includes(request.status)) {
            this.handleUnauthorizedAccess(request);
        } else if (request.status === 302) {
            this.handleRedirect(request);
        }
    }

    private isRedirectLoop(request: HttpErrorResponse): boolean {
        return window.location.href.includes('not-found') || (request.status === 302 && this.tenantId == null && request.error.redirectUrl.includes('not-found'));
    }

    private handleUnauthorizedAccess(request: HttpErrorResponse) {
        if (this.tenantId == null) {
            return;
        }

        this.hasPermissions$$.next(false);
        window.location.href = '/not-found';
    }

    private handleRedirect(request: HttpErrorResponse) {
        window.location.href = request.error.redirectUrl;
    }
}
